<template>
  <div class="alarm-log d-flex flex-column flex-grow-1 min-h-0">
    <div class="filter-bar">
      <div class="filter-item filter-item--category">
        <div class="seg-tabs">
          <button v-for="item in categoryOptions" :key="item.value || 'all'" type="button" class="seg-tab"
            :class="{ active: filters.category === item.value }" :disabled="loading"
            @click="selectCategory(item.value)">
            {{ item.title }}
          </button>
        </div>
      </div>

      <div class="filter-item filter-item--date">
        <el-date-picker v-model="filters.dateRange" type="daterange" range-separator="至" start-placeholder="开始时间"
          end-placeholder="结束时间" value-format="YYYY-MM-DD" :clearable="false" :disabled-date="disabledDate"
          class="filter-date-picker" @calendar-change="handleCalendarChange" @change="syncDateRange" />
      </div>

      <div class="filter-item filter-item--description">
        <v-text-field v-model="filters.description" placeholder="请输入告警描述" variant="outlined" density="compact"
          hide-details clearable rounded="xl" class="filter-input" @keyup.enter="handleSearch" />
      </div>

      <div class="filter-actions">
        <v-btn color="#2655f1" rounded="xl" class="action-btn" prepend-icon="mdi-magnify" :loading="loading"
          :disabled="exportLoading" @click="handleSearch">
          查询
        </v-btn>
        <v-btn :variant="hasAdvancedFilters ? 'flat' : 'outlined'" color="#2655f1" rounded="xl"
          :class="['action-btn', { 'action-btn--active': hasAdvancedFilters }]" prepend-icon="mdi-filter-variant"
          :disabled="loading || exportLoading" @click="openAdvancedFilterDialog">
          筛选
        </v-btn>
        <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" prepend-icon="mdi-refresh"
          :disabled="loading || exportLoading" @click="handleReset">
          重置
        </v-btn>
        <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" prepend-icon="mdi-download"
          :loading="exportLoading" :disabled="loading || !canExportAlarmLog" @click="handleExport">
          导出
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="advancedFilterDialogVisible" class="advanced-filter-dialog gm-dialog" max-width="420"
      scrim="rgba(15, 23, 42, 0.24)" transition="slide-x-reverse-transition">
      <v-card class="advanced-filter-card overflow-hidden">
        <div class="advanced-filter-header d-flex align-center px-8 py-6">
          <div class="text-h5"><span class="advanced-filter-title-accent">筛选</span><span>条件</span></div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)"
            @click="closeAdvancedFilterDialog" />
        </div>

        <div class="advanced-filter-body px-8 pt-4 pb-2">
          <div class="advanced-filter-item">
            <div class="advanced-filter-label">电站名称或ID</div>
            <v-text-field v-model="advancedFilterDraft.stationKeyword" placeholder="请输入电站名称或ID"
              variant="outlined" density="compact" hide-details clearable rounded="xl" class="gm-input"
              @keyup.enter="applyAdvancedFilters" />
          </div>
          <div class="advanced-filter-item">
            <div class="advanced-filter-label">告警编码</div>
            <v-text-field v-model="advancedFilterDraft.alarmCode" placeholder="请输入告警编码" variant="outlined"
              density="compact" hide-details clearable rounded="xl" class="gm-input"
              @keyup.enter="applyAdvancedFilters" />
          </div>
          <div class="advanced-filter-item">
            <div class="advanced-filter-label">恢复状态</div>
            <v-select v-model="advancedFilterDraft.onlyUnrecovered" :items="recoveryStateOptions" placeholder="全部"
              variant="outlined" density="compact" hide-details rounded="xl" class="gm-input"
              :menu-props="{ contentClass: 'alarm-log-filter-select-menu' }" />
          </div>
        </div>

        <div class="advanced-filter-actions px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="advanced-filter-reset px-10 mr-4"
            @click="resetAdvancedFiltersOnly">
            重置筛选
          </v-btn>
          <v-btn color="#2655f1" variant="flat" class="advanced-filter-confirm px-10 text-white"
            @click="applyAdvancedFilters">
            确定
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <div class="alarm-log__table flex-grow-1 min-h-0 d-flex flex-column" :class="{ 'is-loading': loading }">
      <!-- 视口隔离层：非滚动容器，v-loading 挂载于此，确保遮罩脱离横向滚动坐标系，始终居中于用户当前视口 -->
      <div
        class="table-scroll-wrapper flex-grow-1 min-h-0"
        v-loading="loading"
        element-loading-text="加载中..."
        element-loading-background="rgba(255, 255, 255, 0.65)"
      >
        <div class="table-scroll">
          <v-table fixed-header class="gm-table alarm-log-table" density="comfortable">
            <thead>
              <tr>
                <th class="fixed-column-left" style="width: 115px; min-width: 115px">操作</th>
                <th style="min-width: 120px">告警分类</th>
                <th style="min-width: 180px; max-width: 280px">告警描述</th>
                <th style="min-width: 130px">告警码</th>
                <th style="min-width: 180px">电站名称</th>
                <th v-show="false" style="min-width: 100px">电站 ID</th>
                <th style="min-width: 140px">设备名称</th>
                <th style="min-width: 160px">设备 UUID</th>
                <th style="min-width: 100px">设备类型</th>
                <th v-show="false" style="min-width: 100px">故障码</th>
                <th v-show="false" style="min-width: 100px">告警等级</th>
                <th v-show="false" style="min-width: 100px">告警状态</th>
                <th style="min-width: 170px">开始时间</th>
                <th style="min-width: 170px">最近触发时间</th>
                <th style="min-width: 170px">结束时间</th>
                <th style="min-width: 90px">触发次数</th>
                <th style="min-width: 90px">恢复次数</th>
                <th style="min-width: 90px">值</th>
                <th style="min-width: 140px">备注</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.key">
                <td class="fixed-column-left">
                  <v-btn
                    size="small"
                    color="#2655f1"
                    variant="flat"
                    class="btn-smart-analysis text-white"
                    @click="handleSmartAnalysis(row)"
                  >
                    <v-icon size="13" class="mr-1">mdi-robot-outline</v-icon>
                    智能分析
                  </v-btn>
                </td>
                <td>
                  <span class="category-cell" :class="getCategoryClass(row.category)">
                    <v-icon size="16">{{ getCategoryIcon(row.category) }}</v-icon>
                    <span>{{ getCategoryText(row.category) }}</span>
                  </span>
                </td>
                <td>
                  <v-tooltip :text="row.description" location="top">
                    <template #activator="{ props }">
                      <div v-bind="props" class="description-cell">{{ row.description }}</div>
                    </template>
                  </v-tooltip>
                </td>
                <td class="mono-cell alarm-code-cell">{{ row.alarmCode }}</td>
                <td>{{ row.stationName }}</td>
                <td v-show="false">{{ row.stationId }}</td>
                <td>{{ row.equipmentName }}</td>
                <td class="mono-cell">{{ row.uuid }}</td>
                <td>{{ getDeviceTypeText(row.type) }}</td>
                <td v-show="false" class="mono-cell">{{ row.code }}</td>
                <td v-show="false">{{ getAlarmLevelText(row.level) }}</td>
                <td v-show="false"><span class="status-tag">未处理</span></td>
                <td>{{ row.startTime }}</td>
                <td>{{ row.lastTime }}</td>
                <td>{{ row.endTime }}</td>
                <td>{{ row.triggerCount }}</td>
                <td>{{ row.recoveryCount }}</td>
                <td>
                  <v-tooltip :text="row.value" location="top" :disabled="!isCellTextLong(row.value)">
                    <template #activator="{ props }">
                      <div v-bind="props" class="value-cell">{{ row.value }}</div>
                    </template>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip :text="row.remark" location="top" :disabled="!isCellTextLong(row.remark)">
                    <template #activator="{ props }">
                      <div v-bind="props" class="value-cell remark-cell-text">{{ row.remark }}</div>
                    </template>
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="!loading && !rows.length">
                <td colspan="15" class="empty-cell">暂无告警日志</td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </div>

      <TablePagination v-if="total > 0" v-model="pageNum" v-model:page-size="pageSize" :total="total"
        :disabled="loading" :has-sizes="true" :has-jump="false" :has-info="true" @change="handlePageChange" />
    </div>

    <!-- 单机离线智能深度分析悬浮抽屉（右侧浮层，不挤开表格） -->
    <OfflineAlarmAnalysisDrawer
      v-model="analysisDrawerVisible"
      :alarm-record="selectedAlarmRecord"
      :table-records="rows"
    />

    <!-- 非离线告警：单告警码证据链深度诊断悬浮抽屉 -->
    <EvidenceDiagnosisDrawer
      v-model="evidenceDrawerVisible"
      :alarm-record="selectedEvidenceRecord"
    />

  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import { saveAs } from 'file-saver'
import { exportAllStationAlertList, getAllStationAlertList } from '@/api/alert'
import TablePagination from '@/components/TablePagination/index.vue'
import { checkPermi } from '@/utils/permission'
import { getAlarmLevelText, getDeviceTypeText } from '@/views/station-monitor/detail/dicts/alert'
import { getCategoryClass, getCategoryIcon, getCategoryLabel } from '@/views/workbench/components/Tasks/AlarmReportDrawer/category'
import OfflineAlarmAnalysisDrawer from './components/OfflineAlarmAnalysisDrawer.vue'
import EvidenceDiagnosisDrawer from './components/EvidenceDiagnosisDrawer.vue'

const analysisDrawerVisible = ref(false)
const selectedAlarmRecord = ref(null)

const evidenceDrawerVisible = ref(false)
const selectedEvidenceRecord = ref(null)

function isOfflineAlarm(row) {
  if (!row) return false
  const code = String(row.alarmCode || row.code || '').trim()
  const desc = String(row.description || '').trim()
  // 匹配单机离线特征：标准告警码 A0_00_04_0002 或描述中明确包含单机离线/设备离线
  return code === 'A0_00_04_0002' || desc.includes('单机离线') || desc.includes('设备离线') || (desc.includes('离线') && !desc.includes('方阵'))
}

function handleSmartAnalysis(row) {
  if (isOfflineAlarm(row)) {
    openOfflineAnalysis(row)
  } else {
    openEvidenceAnalysis(row)
  }
}

function openOfflineAnalysis(row) {
  selectedAlarmRecord.value = row
  analysisDrawerVisible.value = true
}

function openEvidenceAnalysis(row) {
  selectedEvidenceRecord.value = row
  evidenceDrawerVisible.value = true
}

const categoryOptions = [
  { title: '影响安全', value: 'SAFETY' },
  { title: '影响收益', value: 'INCOME' },
  { title: '普通告警', value: 'NORMAL' },
  { title: '全部', value: '' },
]

const categoryText = {
  SAFETY: '影响安全',
  INCOME: '影响收益',
  NORMAL: '普通告警',
}

const filters = reactive({
  dateRange: [],
  startTime: '',
  endTime: '',
  category: 'SAFETY',
  stationKeyword: '',
  alarmCode: '',
  description: '',
  onlyUnrecovered: true,
})

const rows = ref([])
const total = ref(0)
const pageNum = ref(1)
const pageSize = ref(10)
const loading = ref(false)
const exportLoading = ref(false)
const rangeAnchor = ref(null)
const lastValidDateRange = ref([])
const canExportAlarmLog = computed(() => checkPermi(['ops:alarmLog:export']))
const MAX_DATE_RANGE_DAYS = 7
const MAX_DATE_RANGE_OFFSET = MAX_DATE_RANGE_DAYS - 1
const advancedFilterDialogVisible = ref(false)
const advancedFilterDraft = reactive({
  stationKeyword: '',
  alarmCode: '',
  onlyUnrecovered: true,
})
const recoveryStateOptions = [
  { title: '全部', value: null },
  { title: '未恢复', value: true },
  { title: '已恢复', value: false },
]
const hasAdvancedFilters = computed(() => Boolean(filters.stationKeyword || filters.alarmCode)
  || filters.onlyUnrecovered !== true)

function getCategoryText(category) {
  return categoryText[String(category || '').toUpperCase()] || getCategoryLabel(category)
}

// 长值/备注超长判断：超过阈值截断省略，悬停查看完整内容
const CELL_TEXT_ELLIPSIS_THRESHOLD = 18
function isCellTextLong(text) {
  return text != null && String(text).length > CELL_TEXT_ELLIPSIS_THRESHOLD
}

function selectCategory(category) {
  if (filters.category === category) return
  filters.category = category
  handleSearch()
}

function syncAdvancedFilterDraft() {
  advancedFilterDraft.stationKeyword = filters.stationKeyword
  advancedFilterDraft.alarmCode = filters.alarmCode
  advancedFilterDraft.onlyUnrecovered = filters.onlyUnrecovered
}

function openAdvancedFilterDialog() {
  syncAdvancedFilterDraft()
  advancedFilterDialogVisible.value = true
}

function closeAdvancedFilterDialog() {
  advancedFilterDialogVisible.value = false
}

function applyAdvancedFilters() {
  filters.stationKeyword = (advancedFilterDraft.stationKeyword || '').trim()
  filters.alarmCode = (advancedFilterDraft.alarmCode || '').trim()
  filters.onlyUnrecovered = advancedFilterDraft.onlyUnrecovered
  closeAdvancedFilterDialog()
  handleSearch()
}

function resetAdvancedFiltersOnly() {
  advancedFilterDraft.stationKeyword = ''
  advancedFilterDraft.alarmCode = ''
  advancedFilterDraft.onlyUnrecovered = true
}

function handleCalendarChange(value) {
  const [start, end] = Array.isArray(value) ? value : []
  rangeAnchor.value = start && !end ? dayjs(start).startOf('day') : null
}

function disabledDate(date) {
  const current = dayjs(date).startOf('day')
  if (current.isAfter(dayjs().endOf('day'))) return true
  if (!rangeAnchor.value) return false

  return current.isBefore(rangeAnchor.value.subtract(MAX_DATE_RANGE_OFFSET, 'day'))
    || current.isAfter(rangeAnchor.value.add(MAX_DATE_RANGE_OFFSET, 'day'))
}

function syncDateRange(value) {
  if (!Array.isArray(value) || value.length !== 2) {
    filters.startTime = ''
    filters.endTime = ''
    return
  }

  const start = dayjs(value[0])
  const end = dayjs(value[1])
  if (end.diff(start, 'day') > MAX_DATE_RANGE_OFFSET) {
    ElMessage.warning(`告警时间最多选择 ${MAX_DATE_RANGE_DAYS} 天`)
    filters.dateRange = [...lastValidDateRange.value]
    rangeAnchor.value = null
    return
  }

  filters.startTime = `${value[0]} 00:00:00`
  filters.endTime = `${value[1]} 23:59:59`
  lastValidDateRange.value = [...value]
  rangeAnchor.value = null
}

function setDefaultDateRange() {
  const today = dayjs().format('YYYY-MM-DD')
  filters.dateRange = [today, today]
  syncDateRange(filters.dateRange)
}

function buildParams(includePagination = true) {
  const stationKeyword = (filters.stationKeyword || '').trim()
  const isStationId = /^\d+$/.test(stationKeyword)

  const params = {
    lastTimeStart: filters.startTime || undefined,
    lastTimeEnd: filters.endTime || undefined,
    category: filters.category || undefined,
    stationName: stationKeyword && !isStationId ? stationKeyword : undefined,
    stationId: isStationId ? stationKeyword : undefined,
    alarmCode: (filters.alarmCode || '').trim() || undefined,
    description: (filters.description || '').trim() || undefined,
    onlyUnrecovered: filters.onlyUnrecovered,
  }

  if (includePagination) {
    params.pageNum = pageNum.value
    params.pageSize = pageSize.value
  }

  return params
}

function normalizeRows(result) {
  const resultData = result?.data || {}
  const list = result?.rows || resultData.rows || resultData.list || []

  return list.map((item, index) => ({
    key: item.id || `${item.stationId || item.station_id || 'station'}-${item.alarmCode || 'alarm'}-${item.startTime || index}-${index}`,
    stationName: item.stationName || item.station_name || '--',
    stationId: item.stationId ?? item.station_id ?? '--',
    equipmentName: item.equipmentName || item.deviceName || '--',
    uuid: item.uuid || item.deviceVid || '--',
    type: item.type || item.deviceType || '',
    category: String(item.category || item.alarmCategory || 'NORMAL').toUpperCase(),
    alarmCode: item.alarmCode || '--',
    code: item.code || '--',
    level: item.level ?? item.alarmLevel,
    description: item.description || item.alarmDescription || '--',
    startTime: item.startTime || '--',
    lastTime: item.lastTime || item.last_time || '--',
    endTime: item.endTime && item.endTime !== '/' ? item.endTime : '--',
    triggerCount: item.triggerCount ?? '--',
    recoveryCount: item.recoveryCount ?? '--',
    value: item.value !== '' && item.value != null ? item.value : '--',
    remark: item.remark || '--',
  }))
}

async function fetchLogs() {
  loading.value = true
  try {
    const result = await getAllStationAlertList(buildParams())
    rows.value = normalizeRows(result)
    total.value = Number(result?.total ?? result?.data?.total ?? 0)
    return rows.value.length > 0 || total.value > 0
  } catch (error) {
    console.error('获取全站告警日志失败:', error)
    rows.value = []
    total.value = 0
    return null
  } finally {
    loading.value = false
  }
}

async function loadPreferredCategory() {
  pageNum.value = 1
  for (const option of categoryOptions) {
    filters.category = option.value
    const hasData = await fetchLogs()
    if (hasData || hasData === null || option.value === '') return
  }
}

async function handleExport() {
  if (!canExportAlarmLog.value) return
  exportLoading.value = true
  try {
    const blob = await exportAllStationAlertList(buildParams(false))
    saveAs(blob, `告警日志_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`)
    ElMessage.success('导出成功')
  } catch (error) {
    console.error('导出全站告警日志失败:', error)
  } finally {
    exportLoading.value = false
  }
}

function handleSearch() {
  pageNum.value = 1
  fetchLogs()
}

function handleReset() {
  filters.stationKeyword = ''
  filters.alarmCode = ''
  filters.description = ''
  filters.onlyUnrecovered = true
  resetAdvancedFiltersOnly()
  closeAdvancedFilterDialog()
  pageNum.value = 1
  pageSize.value = 10
  setDefaultDateRange()
  loadPreferredCategory()
}

function handlePageChange(page) {
  pageNum.value = page
  fetchLogs()
}

onMounted(() => {
  setDefaultDateRange()
  loadPreferredCategory()
})
</script>

<style scoped lang="scss">
.alarm-log {
  height: 100%;
  min-width: 0;
  background: #fff;
  overflow: hidden;
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  min-height: 72px;
  padding: 16px 24px;
  background: #fff;
}

.filter-item {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-width: 0;
  height: 40px;

  &--date {
    flex-basis: 360px;
    width: 360px;
  }

  &--description {
    flex-basis: 280px;
    width: 280px;
  }

  &--category {
    flex: 0 0 auto;
    height: 40px;
  }
}

.seg-tabs {
  display: inline-flex;
  flex-shrink: 0;
  padding: 4px;
  gap: 8px;
  border: 1px solid rgba(58, 104, 255, 0.08);
  border-radius: 999px;
  background: #f3f6ff;
  box-shadow: 0 4px 12px rgba(58, 104, 255, 0.08);
}

.seg-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 56px;
  height: 32px;
  padding: 6px 12px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(46, 46, 46, 0.65);
  font-family: 'PingFang SC', 'HarmonyOS Sans SC', sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  transition: color 0.2s, background 0.2s, box-shadow 0.2s;

  &:hover {
    color: #2e2e2e;
  }

  &:disabled {
    cursor: wait;
  }

  &.active {
    color: #fff;
    background: #3a68ff;
    box-shadow: 0 6px 16px rgba(58, 104, 255, 0.35);
  }
}

.filter-date-picker {
  flex: 0 0 100%;
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;

  :deep(.el-input__wrapper) {
    width: 100% !important;
    min-height: 40px;
    height: 40px;
    padding: 0 16px;
    border: 1px solid rgba(192, 207, 218, 0.3);
    border-radius: 20px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  :deep(.el-range-input) {
    color: #2e2e2e;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
  }
}

:deep(.el-range-editor) {
  height: 40px !important;
  min-height: 40px !important;
  border: 1px solid rgba(192, 207, 218, 0.3);
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

:deep(.el-range-editor:focus-within) {
  border-color: var(--color-primary);
}

.filter-select,
.filter-input {
  flex: 0 0 100%;
  width: 100%;
  min-width: 100%;
  max-width: 100%;

  :deep(.v-field) {
    min-height: 40px !important;
    height: 40px !important;
    border: 1px solid rgba(192, 207, 218, 0.3);
    border-radius: 20px;
    display: flex;
    align-items: center;
    margin: 0;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  :deep(.v-field__input) {
    min-height: 40px;
    padding: 8px 16px;
    color: #2e2e2e;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
  }

  :deep(input::placeholder) {
    color: rgba(46, 46, 46, 0.5);
  }
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  height: 40px;
  margin-left: auto;
}

.action-btn {
  min-height: 40px;
  text-transform: none;
}

.action-btn--active {
  color: #fff !important;
}

.advanced-filter-card {
  height: 100vh;
  max-height: 100vh;
  border-radius: 30px 0 0 30px !important;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: #fff;
}

.advanced-filter-header {
  flex-shrink: 0;
  background: linear-gradient(185deg, rgba(38, 85, 241, 0.38) 0%, rgba(255, 255, 255, 0) 60%);

  .text-h5 {
    font-size: 24px !important;
    letter-spacing: 0;
    font-weight: 500;
  }
}

.advanced-filter-title-accent {
  color: #2655f1;
}

.advanced-filter-body {
  flex: 1 1 auto;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
}

.advanced-filter-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.advanced-filter-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}

.advanced-filter-actions {
  display: flex;
  flex-shrink: 0;
}

.advanced-filter-reset,
.advanced-filter-confirm {
  height: 42px !important;
  border-radius: 12px !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
  text-transform: none;
}

.advanced-filter-reset {
  color: #545454 !important;
}

.advanced-filter-dialog :deep(.v-overlay__content) {
  margin: 0 !important;
  left: auto !important;
  right: 0 !important;
  width: min(420px, 100vw) !important;
  max-width: min(420px, 100vw) !important;
  height: 100% !important;
  max-height: 100% !important;
}

.alarm-log__table {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border: 1px solid #e8edf6;
  border-radius: 8px;
  background: #fff;
  margin: 0 16px 16px;
  position: relative;
}

/* 视口隔离层：非滚动容器，专供 loading 遮罩定位，彻底脱离宽表格滚动偏移量 */
.table-scroll-wrapper {
  position: relative;
  overflow: hidden;
  flex: 1 1 0;
  min-height: 200px;
  min-width: 0;
  width: 100%;

  :deep(.el-loading-mask) {
    z-index: 20 !important;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.el-loading-spinner) {
    position: static;
    margin-top: 0;
    width: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .el-loading-text {
      margin-top: 8px;
      font-size: 13px;
      font-weight: 500;
      color: #2655f1;
    }

    .circular {
      width: 40px;
      height: 40px;

      .path {
        stroke: #2655f1;
        stroke-width: 3;
      }
    }
  }
}

/* 绝对定位滚动容器：由内部承接宽表格横纵向自由滚动，不影响视口遮罩居中 */
.table-scroll {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  inset: 0;
  overflow: auto;
}

.alarm-log__table :deep(.table-pagination) {
  flex: 0 0 auto;
}

.alarm-log-table {
  min-width: max-content;

  :deep(.v-table__wrapper) {
    overflow: visible;
  }

  :deep(table) {
    width: 100%;
    min-width: max-content;
  }

  :deep(th) {
    height: 48px;
    color: #536078;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8faff !important;
    box-shadow: 0 1px 0 #e8edf6;
  }

  :deep(td) {
    height: 54px;
    color: #35415c;
    font-size: 13px;
    white-space: nowrap;
  }

  :deep(th.fixed-column-left) {
    position: sticky;
    left: 0;
    z-index: 4 !important;
    background: #f8faff !important;
    box-shadow: 2px 0 6px rgba(0, 0, 0, 0.05), 0 1px 0 #e8edf6;
    text-align: center;
  }

  :deep(td.fixed-column-left) {
    position: sticky;
    left: 0;
    z-index: 3 !important;
    background: #ffffff !important;
    box-shadow: 2px 0 6px rgba(0, 0, 0, 0.05);
    text-align: center;
  }

  :deep(tr:hover td.fixed-column-left) {
    background: #f8faff !important;
  }

  .btn-smart-analysis {
    font-size: 12px;
    font-weight: 600;
    border-radius: 6px;
    height: 28px !important;
    padding: 0 10px;
    letter-spacing: 0;
    box-shadow: 0 2px 6px rgba(38, 85, 241, 0.25);
    transition: all 0.2s;

    &:hover {
      box-shadow: 0 4px 10px rgba(38, 85, 241, 0.35);
      transform: translateY(-1px);
    }
  }
}

.mono-cell {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
}

.description-cell {
  // width: 280px;
  // max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 值/备注长文本：固定列宽 + 省略号 + 悬停查看完整值 */
.value-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remark-cell-text {
  max-width: 260px;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}

.category-cell {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;

  &.cat-safety {
    color: rgba(255, 30, 30, 1);
  }

  &.cat-income {
    color: rgba(245, 158, 11, 1);
  }

  &.cat-normal {
    color: rgba(58, 104, 255, 1);
  }
}

.alarm-code-cell {
  color: #30416f;
  font-weight: 500;
}

.status-tag {
  color: #c2410c;
  background: #fff7ed;
}

.empty-cell {
  height: 220px !important;
  color: #8b95a7 !important;
  text-align: center;
}

@media (max-width: 1440px) {
  .filter-bar {
    padding: 12px 16px;
    gap: 10px;
  }

  .filter-item {
    &--category {
      width: 320px;
    }

    &--date {
      width: 360px;
      flex-basis: 360px;
    }

    &--description {
      width: 280px;
      flex-basis: 280px;
    }
  }
}

@media (max-width: 1220px) {
  .filter-actions {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 760px) {
  .filter-bar {
    padding: 12px 16px;
  }

  .filter-item,
  .filter-item--date {
    flex-basis: 100%;
    width: 100%;
    max-width: 100%;
  }

  .alarm-log__table {
    margin: 0 12px 12px;
  }
}
</style>
